import {Trans} from '@lingui/macro';
import React, {Component, createRef, RefObject} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {
  ClickEventHandler,
  EventHandler,
  FrontClickEvent,
  Handler,
  HandlerOf,
} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonChevronContent} from '../../../../../../libs/shared/ui/src/components/buttons/buttonChevronContent';
import {
  ButtonChrome,
  ButtonInheritableProps,
} from '../../../../../../libs/shared/ui/src/components/buttons/buttonChrome';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {KeyboardShortcuts} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {RepositionPopoverProps} from '../../../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisuallyHidden} from '../../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {PaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/paletteModel';
import {startSimplePerformanceInteraction} from '../../../../../../srcCommon/services/performanceMetrics/interactionPerformance';
import {PerformanceInteractionTypesEnum} from '../../../../../../srcCommon/services/performanceMetrics/interactionPerformanceTypes';
import {PermissionResult} from '../../../../../helpers/permissionHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {SendActionsEnum, SendMessageOptionsModel} from '../../../../../models/api/messageModel';
import {DefaultSendActionsEnum, OnboardingTipTypesEnum} from '../../../../../models/api/preferencesModel';
import {
  InterfaceEventsEnum,
  SnoozeLocationsEnum,
  trackInterfaceEvent,
} from '../../../../../services/tracking';
import {PermissionSimpleDropdownItem} from '../../../../common/dropdowns/permissionSimpleDropdownItem';
import {InfoTipContainer} from '../../../../common/infoTips/infoTipContainer';
import {GuidedDemoComposerSendInfoBubble} from '../../../../infoBubbles/guidedDemoComposerSendInfoBubble';
import {SafetyNetActionsEnum} from '../../../viewer/conversation/safetyNets/safetyNetsContext';
import {SafetyNetsCoordinator} from '../../../viewer/conversation/safetyNets/safetyNetsCoordinator';
import {ReminderDropdownContainer} from '../dropdowns/reminderDropdownContainer';
import {SendLaterDropdownContainer} from '../dropdowns/sendLaterDropdownContainer';

/*
 * Props.
 */

export type SendMessage = (options?: Partial<SendMessageOptionsModel>) => void;

export interface SendToolbarButtonProps {
  hasValidationErrors?: boolean;
  areSendShortcutsDisabled?: boolean;
  hasClosePermission: boolean;
  hasSendPermission: boolean;
  updateConversationStatusOfflinePermission: PermissionResult;
  defaultSendAction: DefaultSendActionsEnum;
  sendMessage: SendMessage;
  shouldShowNewUserTooltip: boolean;
  conversation: ConversationModel | null;
}

/*
 * Style.
 */

const StyledButtonDiv = styled.div`
  margin: 0 3px;
`;

const StyledChevronButton = styled(ChromelessButton)`
  display: flex;
  align-items: stretch;
`;

/*
 * Component.
 */

interface SendToolbarButtonState {
  activeDropdownAnchor?: SendToolbarButtonDropdownAnchorsEnum;
  activeDropdownItem?: SendToolbarButtonDropdownItemsEnum;
}

enum SendToolbarButtonDropdownAnchorsEnum {
  /** Dropdown is opened from the main part of the button. */
  DEFAULT = 'DEFAULT',
  /** Dropdown is opened from the chevron part of the button. */
  OPTIONS = 'OPTIONS',
}

enum SendToolbarButtonDropdownItemsEnum {
  /** Later time subdropdown is open. */
  SEND_LATER = 'SEND_LATER',
  /** Snooze time subdropdown is open. */
  SEND_AND_SNOOZE = 'SEND_AND_SNOOZE',
}

type SendToolbarButtonDropdownAnchorMap = {
  [key in SendToolbarButtonDropdownAnchorsEnum]: RefObject<HTMLDivElement>;
};

export class SendToolbarButton extends Component<SendToolbarButtonProps, SendToolbarButtonState> {
  constructor(props: SendToolbarButtonProps) {
    super(props);

    this.state = {};
    this.chromeRef = createRef<HTMLDivElement>();
    this.buttonRefs = {
      [SendToolbarButtonDropdownAnchorsEnum.DEFAULT]: createRef<HTMLDivElement>(),
      [SendToolbarButtonDropdownAnchorsEnum.OPTIONS]: createRef<HTMLDivElement>(),
    };
  }

  private chromeRef: RefObject<HTMLDivElement>;
  private buttonRefs: SendToolbarButtonDropdownAnchorMap;

  private closeDropdown() {
    this.setState({activeDropdownAnchor: undefined, activeDropdownItem: undefined});
  }

  /*
   * Event handlers.
   */

  private readonly onDefaultButtonClick: ClickEventHandler = (event) => {
    startSimplePerformanceInteraction(event, PerformanceInteractionTypesEnum.SEND_MESSAGE);

    switch (this.props.defaultSendAction) {
      case DefaultSendActionsEnum.SEND:
        this.onSendClick();
        break;

      case DefaultSendActionsEnum.SEND_AND_ARCHIVE:
        this.onSendAndArchiveClick();
        break;

      case DefaultSendActionsEnum.SEND_AND_SNOOZE:
        this.onSendAndSnoozeClick();
        break;

      default:
    }
  };

  private readonly onOptionsButtonClick: Handler = () => {
    this.setState({
      activeDropdownAnchor: SendToolbarButtonDropdownAnchorsEnum.OPTIONS,
      activeDropdownItem: undefined,
    });

    trackInterfaceEvent(InterfaceEventsEnum.OPEN_MESSAGE_SEND_OPTIONS);
  };

  private readonly onDropdownRequestClose: Handler = () => {
    this.closeDropdown();
  };

  private readonly onSendLaterItemSelect: EventHandler = (event) => {
    this.setState({activeDropdownItem: SendToolbarButtonDropdownItemsEnum.SEND_LATER});
    event.preventDefault();
  };

  private readonly onSendAndSnoozeItemSelect: EventHandler = (event) => {
    this.setState({activeDropdownItem: SendToolbarButtonDropdownItemsEnum.SEND_AND_SNOOZE});
    event.preventDefault();
  };

  private readonly onSendClick = () => {
    this.props.sendMessage({sendAction: SendActionsEnum.SEND});
  };

  private readonly onSendAndArchiveClick = () => {
    this.props.sendMessage({sendAction: SendActionsEnum.SEND_AND_ARCHIVE});
  };

  private readonly onSendAndSnoozeClick = () => {
    const {defaultSendAction} = this.props;
    const activeDropdownAnchor =
      defaultSendAction === DefaultSendActionsEnum.SEND_AND_SNOOZE
        ? SendToolbarButtonDropdownAnchorsEnum.DEFAULT
        : SendToolbarButtonDropdownAnchorsEnum.OPTIONS;

    this.setState({
      activeDropdownAnchor,
      activeDropdownItem: SendToolbarButtonDropdownItemsEnum.SEND_AND_SNOOZE,
    });
  };

  private readonly onSendLaterTimeSelect: HandlerOf<number> = (sendLaterAt) => {
    this.closeDropdown();
    this.props.sendMessage({sendLaterAt});

    // Tracking.
    const delay = Math.round((sendLaterAt - Date.now()) / 1000);
    trackInterfaceEvent(InterfaceEventsEnum.MESSAGE_SENT_LATER, {delay});
  };

  private readonly onSendAndSnoozeTimeSelect: HandlerOf<number> = (remindAt) => {
    this.closeDropdown();
    this.props.sendMessage({remindAt, sendAction: SendActionsEnum.SEND_AND_ARCHIVE});
  };

  /*
   * Render.
   */

  render() {
    const {defaultSendAction, hasValidationErrors, hasSendPermission, areSendShortcutsDisabled} = this.props;
    const isSendDisabled = !hasSendPermission || hasValidationErrors;

    const keyHandlers = {
      [KeyboardActionsEnum.SEND]: this.onSendClick,
      [KeyboardActionsEnum.SEND_AND_SNOOZE]: this.onSendAndSnoozeClick,
    };

    const isButtonSendAndSnooze = defaultSendAction === DefaultSendActionsEnum.SEND_AND_SNOOZE;
    const shouldHideInfoTip =
      !this.props.shouldShowNewUserTooltip ||
      isSendDisabled ||
      defaultSendAction !== DefaultSendActionsEnum.SEND_AND_ARCHIVE;

    const variant = isButtonSendAndSnooze ? ButtonVariantsEnum.CUSTOM_DISABLED : ButtonVariantsEnum.PRIMARY;
    const color = isButtonSendAndSnooze ? PaletteColorsEnum.ORANGE : undefined;

    return (
      <SafetyNetsCoordinator
        conversations={this.props.conversation ? [this.props.conversation] : []}
        safetyNetActionType={SafetyNetActionsEnum.SEND_AND_ARCHIVE}
        onActionValidated={this.onSendAndArchiveClick}
      >
        {(onSendAndArchiveClick) => (
          <KeyboardShortcuts
            handlers={{...keyHandlers, [KeyboardActionsEnum.SEND_AND_DONE]: onSendAndArchiveClick}}
            isDisabled={isSendDisabled || areSendShortcutsDisabled}
          >
            <TooltipCoordinator render={() => this.maybeRenderTooltip()}>
              <InfoTipContainer
                tipType={OnboardingTipTypesEnum.SEND_AND_ARCHIVE}
                title={<Trans id="send-toolbar-button-newusertitle">Send and Archive</Trans>}
                description={
                  <Trans id="send-toolbar-button-newuserdescription">
                    When you&apos;re done, you can archive your message so it&apos;s off your plate.
                    (Don&apos;t worry: If someone replies, it reopens automatically.)
                  </Trans>
                }
                shouldHide={shouldHideInfoTip}
                placement="top-end"
              >
                <StyledButtonDiv>
                  <ButtonChrome
                    ref={this.chromeRef}
                    variant={variant}
                    isDisabled={isSendDisabled}
                    color={color}
                  >
                    {(buttonProps) => (
                      <>
                        {this.renderDefaultButton(buttonProps)}
                        {this.renderOptionsButton(buttonProps)}
                      </>
                    )}
                  </ButtonChrome>
                </StyledButtonDiv>
              </InfoTipContainer>
            </TooltipCoordinator>
            {this.maybeRenderDropdown()}
          </KeyboardShortcuts>
        )}
      </SafetyNetsCoordinator>
    );
  }

  private renderDefaultButton(buttonProps: ButtonInheritableProps) {
    const {activeDropdownAnchor} = this.state;
    const isDropdownOpen = activeDropdownAnchor === SendToolbarButtonDropdownAnchorsEnum.DEFAULT;

    return (
      <TooltipCoordinator render={() => this.renderDefaultButtonContent()}>
        <SafetyNetsCoordinator
          conversations={this.props.conversation ? [this.props.conversation] : []}
          onActionValidated={(event: FrontClickEvent) => this.onDefaultButtonClick(event)}
          safetyNetActionType={getSafetyNetActionFromDefaultSendAction(this.props.defaultSendAction)}
        >
          {(onDefaultButtonClick) => (
            <ChromelessButton
              {...buttonProps}
              ref={this.buttonRefs[SendToolbarButtonDropdownAnchorsEnum.DEFAULT]}
              onClick={onDefaultButtonClick}
              hasHover={isDropdownOpen}
              isOpen={isDropdownOpen}
              interactionId="send-toolbar-button"
            >
              <ButtonContent {...buttonProps}>
                <TooltipOverflowCondition>{this.renderDefaultButtonContent()}</TooltipOverflowCondition>
              </ButtonContent>
            </ChromelessButton>
          )}
        </SafetyNetsCoordinator>
        <GuidedDemoComposerSendInfoBubble
          targetRef={this.buttonRefs[SendToolbarButtonDropdownAnchorsEnum.DEFAULT]}
        />
      </TooltipCoordinator>
    );
  }

  private renderDefaultButtonContent() {
    switch (this.props.defaultSendAction) {
      case DefaultSendActionsEnum.SEND:
        return renderSend();

      case DefaultSendActionsEnum.SEND_AND_ARCHIVE:
        return renderSendAndArchive();

      case DefaultSendActionsEnum.SEND_AND_SNOOZE:
        return renderSendAndSnooze();

      default:
        return null;
    }
  }

  private renderOptionsButton(buttonProps: ButtonInheritableProps) {
    const {activeDropdownAnchor} = this.state;
    const isDropdownOpen = activeDropdownAnchor === SendToolbarButtonDropdownAnchorsEnum.OPTIONS;

    return (
      <StyledChevronButton
        {...buttonProps}
        ref={this.buttonRefs[SendToolbarButtonDropdownAnchorsEnum.OPTIONS]}
        onClick={this.onOptionsButtonClick}
        hasHover={isDropdownOpen}
        isOpen={isDropdownOpen}
      >
        <ButtonChevronContent {...buttonProps}>
          <Icon name="chevronDownSmall" />
          <VisuallyHidden>
            <Trans id="composer-send-toggleoptions">Toggle send options</Trans>
          </VisuallyHidden>
        </ButtonChevronContent>
      </StyledChevronButton>
    );
  }

  /** Render a dropdown for secondary send actions. */
  private maybeRenderDropdown() {
    const {activeDropdownAnchor, activeDropdownItem} = this.state;
    if (!activeDropdownAnchor) {
      return null;
    }

    const dropdownProps: RepositionPopoverProps = {
      anchor: this.chromeRef,
      onRequestClose: this.onDropdownRequestClose,
      placement: 'top-end',
    };

    switch (activeDropdownItem) {
      case SendToolbarButtonDropdownItemsEnum.SEND_LATER:
        return this.renderSendLaterDropdown(dropdownProps);

      case SendToolbarButtonDropdownItemsEnum.SEND_AND_SNOOZE:
        return this.renderSendAndSnoozeDropdown(dropdownProps);

      default:
        return this.renderOptionsDropdown(dropdownProps);
    }
  }

  private renderSendLaterDropdown(dropdownProps: RepositionPopoverProps) {
    return (
      <SendLaterDropdownContainer
        {...dropdownProps}
        title={<Trans id="composer-send-later">Send Later</Trans>}
        selectButton={<Trans id="composer-send-later-button">Send Later</Trans>}
        onValueSelect={(snoozeAt) => {
          this.onSendLaterTimeSelect(snoozeAt);

          if (dropdownProps.onRequestClose) {
            dropdownProps.onRequestClose();
          }
        }}
      />
    );
  }

  private renderSendAndSnoozeDropdown(dropdownProps: RepositionPopoverProps) {
    return (
      <ReminderDropdownContainer
        {...dropdownProps}
        title={renderSendAndSnooze()}
        selectButton={<Trans id="composer-send">Send</Trans>}
        onValueSelect={(snoozeAt) => {
          this.onSendAndSnoozeTimeSelect(snoozeAt);

          if (dropdownProps.onRequestClose) {
            dropdownProps.onRequestClose();
          }
        }}
        location={SnoozeLocationsEnum.SEND_AND_SNOOZE}
      />
    );
  }

  private renderOptionsDropdown(dropdownProps: RepositionPopoverProps) {
    const {hasClosePermission, updateConversationStatusOfflinePermission} = this.props;

    return (
      <SimpleDropdown {...dropdownProps}>
        <SimpleDropdownItem onSelect={this.onSendClick}>{renderSend()}</SimpleDropdownItem>
        <SafetyNetsCoordinator
          conversations={this.props.conversation ? [this.props.conversation] : []}
          onActionValidated={() => this.onSendAndArchiveClick()}
          safetyNetActionType={SafetyNetActionsEnum.SEND_AND_ARCHIVE}
        >
          {(onSendAndArchiveClick) => (
            <PermissionSimpleDropdownItem
              offlinePermission={updateConversationStatusOfflinePermission}
              onSelect={onSendAndArchiveClick}
              isDisabled={!hasClosePermission}
            >
              {renderSendAndArchive()}
            </PermissionSimpleDropdownItem>
          )}
        </SafetyNetsCoordinator>
        <SimpleDropdownItem onSelect={this.onSendLaterItemSelect}>
          <Trans id="composer-send-later">Send Later</Trans>
        </SimpleDropdownItem>
        <PermissionSimpleDropdownItem
          offlinePermission={updateConversationStatusOfflinePermission}
          onSelect={this.onSendAndSnoozeItemSelect}
        >
          {renderSendAndSnooze()}
        </PermissionSimpleDropdownItem>
      </SimpleDropdown>
    );
  }

  /** If the button is disabled, add a tooltip to explain why. */
  private maybeRenderTooltip() {
    if (this.props.hasSendPermission) {
      return null;
    }

    return <Trans id="composer-send-nopermission">You do not have permission to send this message</Trans>;
  }
}

function renderSend() {
  return <Trans id="composer-send">Send</Trans>;
}

function renderSendAndArchive() {
  return <Trans id="composer-send-archive">Send &amp; Archive</Trans>;
}

function renderSendAndSnooze() {
  return <Trans id="composer-send-snooze">Send &amp; Snooze</Trans>;
}

const defaultSendActionSafetyNetActionMap: Record<DefaultSendActionsEnum, SafetyNetActionsEnum> = {
  [DefaultSendActionsEnum.SEND]: SafetyNetActionsEnum.SEND,
  [DefaultSendActionsEnum.SEND_AND_ARCHIVE]: SafetyNetActionsEnum.SEND_AND_ARCHIVE,
  [DefaultSendActionsEnum.SEND_AND_SNOOZE]: SafetyNetActionsEnum.SEND_AND_SNOOZE,
};

function getSafetyNetActionFromDefaultSendAction(
  defaultSendAction: DefaultSendActionsEnum,
): SafetyNetActionsEnum {
  return defaultSendActionSafetyNetActionMap[defaultSendAction];
}
